/**
 * Perakit frame orb: membersihkan kanvas, menggambar cahaya latar, lalu
 * menyerahkan sisanya ke penggambar varian yang sesuai.
 */

import type { OrbVariant } from "@/types/ai-assistant-orb";
import { type Rgb, rgba, sampleRamp } from "./orb-colors";
import { type OrbFrame, paramOr, RADIUS_RATIO } from "./orb-frame";
import { resolveVariantForSize } from "./orb-geometry";
import { ORB_PAINTERS } from "./orb-painters";

/** Cahaya latar di belakang bola; satu gradien radial, bukan blur per titik. */
function drawBackglow(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	radius: number,
	color: Rgb,
	strength: number,
): void {
	if (strength <= 0) return;
	const outer = radius * 1.55;
	const gradient = ctx.createRadialGradient(
		cx,
		cy,
		radius * 0.2,
		cx,
		cy,
		outer,
	);
	gradient.addColorStop(0, rgba(color, 0.38 * strength));
	gradient.addColorStop(1, rgba(color, 0));
	ctx.fillStyle = gradient;
	ctx.fillRect(cx - outer, cy - outer, outer * 2, outer * 2);
}

/**
 * Menggambar satu frame orb. `offset` menggeser pusat beberapa piksel untuk
 * mitigasi burn-in; kanvas dibersihkan lebih dulu sehingga frame tidak menumpuk.
 */
export function renderOrbFrame(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	variant: OrbVariant,
	frame: OrbFrame,
	offset: { x: number; y: number } = { x: 0, y: 0 },
): void {
	ctx.clearRect(0, 0, width, height);
	if (width <= 0 || height <= 0) return;
	const radius = Math.min(width, height) * RADIUS_RATIO;
	const cx = width / 2 + offset.x;
	const cy = height / 2 + offset.y;
	const effective = resolveVariantForSize(variant, frame.cssSize);

	drawBackglow(
		ctx,
		cx,
		cy,
		radius,
		sampleRamp(frame.ramp, 0.5),
		(paramOr(frame.params, "glow", 45) / 100) * frame.brightness,
	);
	ORB_PAINTERS[effective](ctx, cx, cy, radius, frame);
}
